import { html, css, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import sharedStyles from '../../app/styles.js';

/**
 * Modal side sheet that slides in from the right, e.g. for filters.
 * The parent owns `opened` and closes the sheet on the `close` event
 * (close button, click on the scrim or Escape).
 *
 * @slot - the sheet's content, stacked vertically at full width
 * @slot actions - buttons at the bottom of the sheet
 * @fires close
 */
@customElement('sdb-side-sheet')
class SdbSideSheet extends LitElement {

    @property({ type: Boolean, reflect: true })
    accessor opened = false;

    @property({ type: String })
    accessor heading = '';

    static styles = [
        sharedStyles,
        css`
            :host {
                position: fixed;
                inset: 0;
                /* Same layer as the editor's overlays; Vaadin dropdowns opened from the sheet
                   are attached to the body later, so they stack above it. */
                z-index: 1300;
                pointer-events: none;
            }

            :host([opened]) {
                pointer-events: auto;
            }

            .scrim {
                position: absolute;
                inset: 0;
                background: rgba(0, 0, 0, 0.32);
                opacity: 0;
                transition: opacity 0.2s;
            }

            :host([opened]) .scrim {
                opacity: 1;
            }

            .sheet {
                position: absolute;
                top: 0;
                right: 0;
                bottom: 0;
                width: 360px;
                max-width: 100%;
                display: flex;
                flex-direction: column;
                background: #fff;
                box-shadow: -4px 0 16px rgba(0, 0, 0, 0.16);
                transform: translateX(100%);
                transition: transform 0.25s ease-out;
            }

            :host([opened]) .sheet {
                transform: none;
            }

            header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 16px 12px 8px 24px;
            }

            h2 {
                margin: 0;
                font-size: 20px;
                font-weight: normal;
                color: var(--text-primary);
            }

            .content {
                flex: 1;
                overflow-y: auto;
                display: flex;
                flex-direction: column;
                gap: 8px;
                padding: 8px 24px 24px;
            }

            ::slotted(*) {
                width: 100%;
            }

            footer {
                display: flex;
                justify-content: flex-end;
                gap: 8px;
                padding: 12px 24px;
                border-top: 1px solid rgb(219, 219, 219);
            }
        `,
    ];

    constructor() {
        super();
        this._onKeyDown = (e) => {
            if (this.opened && e.key === 'Escape') {
                this._close();
            }
        };
    }

    connectedCallback() {
        super.connectedCallback();
        window.addEventListener('keydown', this._onKeyDown);
    }

    disconnectedCallback() {
        window.removeEventListener('keydown', this._onKeyDown);
        super.disconnectedCallback();
    }

    render() {
        return html`
            <div class="scrim" @click=${this._close}></div>
            <aside
                class="sheet"
                role="dialog"
                aria-modal="true"
                aria-label=${this.heading}
                ?inert=${!this.opened}>
                <header>
                    <h2>${this.heading}</h2>
                    <sdb-content-iconbutton
                        icon="fal fa-xmark"
                        @click=${this._close}>
                    </sdb-content-iconbutton>
                </header>
                <div class="content"><slot></slot></div>
                <footer><slot name="actions"></slot></footer>
            </aside>
        `;
    }

    _close() {
        this.dispatchEvent(new CustomEvent('close'));
    }
}
